import * as THREE from 'three';
import type { SwingPathMetrics } from '../input/ShotIntent';
import { REFERENCE_GREEN_DECELERATION } from './GreenSpeedProfile';

export interface PuttStrokeMetrics {
  readonly normalizedStrokeLength: number;
  readonly paceScalar: number;
  readonly lineErrorRadians: number;
  readonly tempoQuality: number;
  readonly strikeQuality: number;
  readonly intendedReferenceDistance: number;
  readonly initialBallSpeed: number;
}

export class PuttStrokeAnalyzer {
  readonly minimumBackswingPixels = 9;
  readonly fullStrokeReferencePixels = 150;

  analyze(
    path: SwingPathMetrics,
    aimRadians = 0,
    paceError = 0,
    lineErrorRadians = 0,
  ): PuttStrokeMetrics {
    const samples = path.samples;
    const origin = samples[0];
    const backswingPixels = Math.max(path.maxBackswingPixels, 0);
    const normalizedStrokeLength = THREE.MathUtils.clamp(
      backswingPixels / this.fullStrokeReferencePixels,
      0,
      1.2,
    );
    const transitionSample = path.transitionPoint
      ? samples.find((p) => p.x === path.transitionPoint?.x && p.y === path.transitionPoint?.y)
      : undefined;
    const impactSample = path.impactPoint
      ? samples.find((p) => p.x === path.impactPoint?.x && p.y === path.impactPoint?.y)
      : undefined;
    const transition =
      transitionSample ?? samples[Math.max(0, Math.floor(samples.length * 0.45))] ?? origin;
    const impact = impactSample ?? samples.at(-1) ?? origin;
    const backDuration = Math.max(
      0.05,
      (transition?.timeSeconds ?? 0) - (origin?.timeSeconds ?? 0),
    );
    const forwardDuration = Math.max(
      0.05,
      (impact?.timeSeconds ?? 0) - (transition?.timeSeconds ?? 0),
    );
    const tempoRatio = backDuration / forwardDuration;
    const tempoQuality = THREE.MathUtils.clamp(1 - Math.abs(tempoRatio - 2.6) / 4.5, 0.55, 1.05);
    const tempoPace = THREE.MathUtils.clamp(
      1 + (1 / Math.max(0.35, tempoRatio) - 1 / 2.6) * 0.16,
      0.88,
      1.12,
    );
    const strikeQuality = THREE.MathUtils.clamp(0.82 + path.straightness * 0.18, 0.72, 1.02);
    const paceScalar = THREE.MathUtils.clamp(
      normalizedStrokeLength * tempoPace * strikeQuality + paceError,
      0,
      1.25,
    );
    const intendedReferenceDistance = PuttLaunchSolver.referenceDistanceForPace(paceScalar);
    const initialBallSpeed = PuttLaunchSolver.speedForReferenceDistance(intendedReferenceDistance);
    const dx = samples.length >= 2 ? samples.at(-1)!.x - samples[0].x : 0;
    const dy = samples.length >= 2 ? samples[0].y - samples.at(-1)!.y : 1;
    const pathRadians = Math.atan2(dx, Math.max(1, dy));
    return {
      normalizedStrokeLength,
      paceScalar,
      lineErrorRadians:
        lineErrorRadians + THREE.MathUtils.clamp(pathRadians - aimRadians, -0.08, 0.08) * 0.35,
      tempoQuality,
      strikeQuality,
      intendedReferenceDistance,
      initialBallSpeed,
    };
  }
}

export class PuttLaunchSolver {
  static referenceDistanceForPace(paceScalar: number): number {
    const p = THREE.MathUtils.clamp(paceScalar, 0, 1.25);
    return 0.22 + 29.5 * Math.pow(p, 1.72);
  }

  static speedForReferenceDistance(distanceMeters: number): number {
    return Math.sqrt(Math.max(0, 2 * REFERENCE_GREEN_DECELERATION * distanceMeters));
  }

  solve(
    metrics: Pick<PuttStrokeMetrics, 'initialBallSpeed' | 'lineErrorRadians'>,
    aimRadians: number,
  ): THREE.Vector3 {
    return new THREE.Vector3(
      Math.sin(aimRadians + metrics.lineErrorRadians),
      0,
      Math.cos(aimRadians + metrics.lineErrorRadians),
    ).multiplyScalar(metrics.initialBallSpeed);
  }

  solveForDistance(
    distanceMeters: number,
    aimRadians: number,
    paceError = 0,
    lineErrorRadians = 0,
  ): THREE.Vector3 {
    const speed = PuttLaunchSolver.speedForReferenceDistance(
      Math.max(0.05, distanceMeters + paceError),
    );
    return new THREE.Vector3(
      Math.sin(aimRadians + lineErrorRadians),
      0,
      Math.cos(aimRadians + lineErrorRadians),
    ).multiplyScalar(speed);
  }
}
